import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Switch, Input, InputNumber, Form, Layout, Button, notification, Select, Popconfirm, Row, Col, Typography, Radio, Spin, Flex, Drawer } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import EnterpriseApiService from 'src/api/EnterpriseApiService';
import BinInfoDTO from 'src/models/BinInfoDTO';
import AdminApiService from 'src/api/AdminApiService';
import ECDeviceDTO from 'src/models/ECDeviceDTO';
import IotModel from 'src/consts/IotModel';
import GrowerDTO from 'src/models/GrowerDTO';
import { useGetAllGrowersQuery } from 'src/queries/useGetAllGrowersQuery';
import { ApiError } from 'src/api/ApiResultHandler';
import { getBinsQueryOptions } from 'src/queries/useGetBinsQuery';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSetState } from 'rooks';
import { PlusOutlined } from '@ant-design/icons';
import { DEBOUNCE_TIME_MS } from '../users/UserList';
import { useDebouncedCallback } from 'use-debounce';
import Table, { ColumnProps } from 'antd/es/table';
import { NotAssignedTag } from './NotAssignedTag';
import IotDeviceDTO from 'src/models/IotDeviceDTO';
import { OptionType } from 'dayjs';
import { DefaultOptionType } from 'antd/es/select';
import { getAllUnusedDBIoTDevicesQueryOptions } from 'src/queries/getAllUnusedDBIoTDevicesQueryOptions';
import { getAllECDevicesOptions } from 'src/queries/getAllECDevicesOptions';
import { getAllIotDevicesQueryOptions } from 'src/queries/useGetAllIotDevicesQuery';

const { Text } = Typography;
const { Option } = Select;

interface Props {
    desiredBin: BinInfoDTO | null;
    setShowForm: (showForm: boolean) => void;
}
  
const AddOrCreateBinForm = (props: Props) => {

    const queryClient = useQueryClient();
    const [loading, setLoading] = useState(false);

    const [form] = Form.useForm<BinInfoDTO>();
    const watchedValues = Form.useWatch([], { form, preserve: true });
    const desiredBin = props.desiredBin;

    const getBinsQueryOptions_ = getBinsQueryOptions(desiredBin?.growerId);

    useEffect( () => {
        if(desiredBin == null){
            form.resetFields();
        }else{
            form.setFieldsValue(desiredBin as any);
        }
    }, [desiredBin]);

    const ecDeviesQuery = useQuery({...getAllECDevicesOptions()});
    const ecDevices = ecDeviesQuery.data ?? [];

    const deleteDesiredBin = async () => {

        setLoading(true);
        try{
            if(desiredBin != null){
                const result = await EnterpriseApiService.deleteBin(desiredBin.id);
                queryClient.invalidateQueries({queryKey: [getBinsQueryOptions().queryKey[0]]});
                notification.success({message:"Bin successfully deleted"});
                form.resetFields();
                props.setShowForm(false);
            }
            
        }catch(error){
            notification.error({
                message: "Could not delete bin",
                description: error?.errorDetails?.detail ?? "Contact support"
            });
        }finally{
            setLoading(false);
        }
    }

    const layout = {
        labelCol: { xs: 10, sm: 10, md: 8, lg: 8, xl: 6},
        wrapperCol: { xs: 16, sm: 16, md: 18, lg: 18, xl: 20 },
    };

    const onFinish = async (values: BinInfoDTO) => 
    {
        try{
            setLoading(true);
            values.peakHeightFt = peakHeight;
            if(values.id == 0 || values.id == null){
                const result = await EnterpriseApiService.addBin(values);
                notification.success({message:"New bin added successfully"})
                props.setShowForm(false);
            }else{
                const result = await EnterpriseApiService.updateBin(values);
                notification.success({message:"Bin updated successfully"})
                props.setShowForm(false);
            }
            queryClient.invalidateQueries({queryKey: [getBinsQueryOptions_.queryKey[0]]});
            queryClient.invalidateQueries({queryKey: ["getAllIotDevices"]});
            queryClient.invalidateQueries({queryKey: getAllIotDevicesQueryOptions().queryKey});
        }catch(error){
            notification.error({
                message: "Could not submit bin information",
                description: error?.errorDetails?.detail ?? "Contact support"
            });
        }finally{
            setLoading(false);
        }
    }

    const getAllIotDevicesQuery = useQuery({...getAllIotDevicesQueryOptions()});
    const iotDevices = getAllIotDevicesQuery.data ?? [];

    const selectedDeviceId = Form.useWatch(['iotDeviceId'], { form, preserve: true });

    const currentIotInfo = desiredBin?.deviceId != null ? {
        azureDeviceId: desiredBin?.deviceId!,
        ecDeviceID: desiredBin?.ecDeviceID!,
        iotDeviceId: desiredBin?.iotDeviceId!,
        iotModel: desiredBin?.iotModel!,
        serialNumber: desiredBin?.serialNumber,
    } : null;

    const getSelectedIotDeviceInfo = (): IotDeviceDTO | null | undefined => {
        if(selectedDeviceId == null){
            return null;
        }
        if (selectedDeviceId == desiredBin?.iotDeviceId) {
            return currentIotInfo;
        }

        return iotDevices.find(x => x.iotDeviceId == selectedDeviceId);
    }

    const selectedIotDeviceInfo = getSelectedIotDeviceInfo();

    const selectedEcDeviceInfo = useMemo(() => {
        const device = ecDevices.find(x => x.id === selectedIotDeviceInfo?.ecDeviceID);
        return device;
    }, [ecDevices, selectedIotDeviceInfo])

    const [peakHeight, setPeakHeight] = useState(0);

    useEffect(() => {
        const calculatePeakHeight = () => {
            let eaveHeight = watchedValues?.eaveHeightFt ?? 0;
            let diameter = watchedValues?.diameterFt ?? 0;
            let radius = (diameter/2.0);
            let peakCapDiameter = watchedValues?.peakCapDiameterFt ?? 0;
            let roofPitch = watchedValues?.roofPitchDegrees ?? 0;
            
            const angleRadians = (roofPitch * Math.PI) / 180.0;
            const tanAngleRadians = Math.tan(angleRadians);
            const peakCapRadius = peakCapDiameter/2.0;
            const radialDifference = radius - peakCapRadius; 
            const height = eaveHeight + (radialDifference * tanAngleRadians);

            const heightFixed = parseFloat(height.toFixed(1));
            return heightFixed;
        };
    
        setPeakHeight(calculatePeakHeight());
    }, [watchedValues]);


    const iotOptions: Array<{label: any, value: number | null} & Partial<IotDeviceDTO>> = [{
        label: <span>No IoT</span>,
        value: null,
    }];
    
    if (currentIotInfo != null) {
        iotOptions.push({
            value: currentIotInfo.iotDeviceId,
            label: <span>{currentIotInfo.iotDeviceId} - {currentIotInfo.azureDeviceId}</span>,
            ...currentIotInfo,
        })
    }
    iotDevices.forEach((iot) => {
        if (iot?.ecDeviceID == null && ENVIRONMENT_NAME === "Production") {
            return;
        }
        iotOptions.push({
            value: iot.iotDeviceId,
            label: <span>{iot.iotDeviceId} - {iot.azureDeviceId}</span>,
            ...iot,
        })
    })

    return (
        <div style={{width: "100%"}}>
            <Spin spinning={loading}>
                <Form initialValues={{
                    isActive: false,
                }}
                onFinish={onFinish} 
                form={form}>
                    <Form.Item name="id"/>
                    <h4>General</h4>
                    <Form.Item {...layout} label="Bin Name" name="name" rules={[{ required: true, message: 'Enter the name of the Bin' }]}>
                        <Input style={{ width: 300 }} />
                    </Form.Item>
                    {/* <Form.Item {...layout} label="Organization" name="growerId" rules={[{ required: true, message: 'Choose the Grower' }]}>
                        <Select style={{ maxWidth: '300px', overflow: "auto", maxHeight: "300px"} } optionFilterProp="children" showSearch >
                            {
                                getAllGrowersQuery.data?.map((grower) => (
                                    <Select.Option key={grower.growerID} value={grower.growerID}>
                                        {grower.growerName}
                                    </Select.Option>
                                ))
                            }
                        </Select>
                    </Form.Item> */}
                    <Form.Item name={"isActive"} {...layout} label="Is Active" valuePropName="checked">
                        <Switch />
                    </Form.Item>

                    <Form.Item {...layout} label="Number" name="number"
                        rules={[{ required: true, message: 'Enter the bin number' }]}>
                        <InputNumber style={{ width:200 }} min={1} />
                    </Form.Item>

                    <h4>Physical characteristics</h4>
                    <Form.Item {...layout} label="Diameter Ft." name="diameterFt"
                        rules={[{ required: true, message: 'Enter the diameter' }]}>
                        <InputNumber style={{ width:200 }} min={0} />
                    </Form.Item>
                    
                    <Form.Item {...layout} label="Roof Pitch" name="roofPitchDegrees"
                        rules={[{ required: true, message: 'Enter the roof pitch' }]}
                        initialValue={32}>
                        <InputNumber style={{ width:200 }} />
                    </Form.Item>

                    <Form.Item {...layout} label="Plenum Height Ft." name="plenumHeightFt"
                        rules={[{ required: true, message: 'Enter the plenum height' }]}
                        initialValue={1}
                        >
                        <InputNumber style={{ width:200 }} min={0} />
                    </Form.Item>
                    
                    <Form.Item {...layout} label="Peak Cap Diameter Ft." name="peakCapDiameterFt"
                        rules={[{ required: true, message: 'Enter the peak cap diameter' }]}
                        initialValue={3}>
                        <InputNumber style={{ width:200 }} min={0} />
                    </Form.Item>

                    <Form.Item {...layout} label="Eave Height Ft." name="eaveHeightFt"
                        rules={[{ required: true, message: 'Enter the eave height' }]}>
                        <InputNumber style={{ width:200 }} />
                    </Form.Item>

                    <Form.Item {...layout} label="Peak Height Ft." name="peakHeightFt">
                        <h4>{peakHeight}</h4>
                    </Form.Item>
                    
                    <Form.Item {...layout} label="Total Estimated Bu Capacity" name="estimatedCapacityBu">
                        <InputNumber style={{ width:200 }} />
                    </Form.Item>

                    <h4>Location</h4>
                    <Form.Item  {...layout} label="Latitude of The Bin" name="binLat"
                        rules={[{ required: true, message: 'Enter the latitude of the Bin' }]}>
                        <InputNumber style={{ width:200 }} max={90} min={-90} />
                    </Form.Item>
                    <Form.Item {...layout} label="Longitude of The Bin" name="binLng"
                        rules={[{ required: true, message: 'Enter the Longitude of the Bin' }]}>
                        <InputNumber style={{ width:200 }} max={180} min={-180} />
                    </Form.Item>
                    
                    <h4>Manufacturer of the bin</h4>
                    <Form.Item {...layout} label="Manufacturer of the Bin" name="manufacturer"
                        rules={[{ required: true, message: 'Enter the Bin Manufacturer' }]}>
                        <Input style={{ width:200 }} />
                    </Form.Item>
                    <Form.Item {...layout} label="Fan Model" name="fanModel"
                        rules={[{ required: true, message: 'Enter the Model of Fan Used' }]}>
                        <Input style={{ width:200 }} />
                    </Form.Item>

                    <h4>Ag Automations connectivity</h4>
                    <Form.Item {...layout} label="Azure IoT Device ID">
                        <Input readOnly disabled value={selectedIotDeviceInfo?.azureDeviceId ?? "No Device"} />
                    </Form.Item>

                    <Form.Item<BinInfoDTO> {...layout} label="EC Device ID">
                        <Input readOnly disabled value={selectedIotDeviceInfo?.ecDeviceID != null ? `${selectedIotDeviceInfo?.ecDeviceID} - ${selectedEcDeviceInfo?.displayName}` : "No Device"} />
                    </Form.Item>

                    <Form.Item<BinInfoDTO> {...layout} label="IoT" name="iotDeviceId">
                        <Select options={iotOptions} style={{ maxWidth: '300px', overflow: "auto", maxHeight: "600px"} } showSearch
                        filterOption={(inputValue, option) => {
                            return option?.azureDeviceId?.toUpperCase().includes(inputValue.toUpperCase()) ?? true
                        }} />
                    </Form.Item>

                    <Form.Item {...layout} label="Iot Model">
                        <Radio.Group disabled value={selectedIotDeviceInfo?.iotModel ?? null}>
                            <Radio value={IotModel.Reliagate}>Reliagate</Radio>
                            <Radio value={IotModel.Compulab_IOTG_IMX8PLUS}>Compulab</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item {...layout} label="Additional Notes" name="notes" rules={[{ required: false, message: 'Enter a Description of the Bin' }]}>
                        <TextArea placeholder="Add any additonal notes about the bin here" />
                    </Form.Item>

                    <Flex justify='space-around'>
                    {desiredBin &&
                                <Popconfirm
                                title="Are you sure want to delete this bin?"
                                onConfirm={() => deleteDesiredBin()}
                                okText="Yes"
                                cancelText="No"
                                >
                                    <Button style={{ marginBottom: '16px' }} type="primary" danger={true}>Delete Bin</Button>
                                </Popconfirm>
                            }
                                            <Form.Item>
                        <Button type="primary" htmlType="submit">
                            Submit
                    </Button>
                    </Form.Item>
                    </Flex>
                </Form>
            </Spin>
    </div>
    );
  };

export const CreateUpdateBinV2 = () => {

    const queryClient = useQueryClient();
    const getAllGrowersQuery = useGetAllGrowersQuery({ enabled:true, refetchOnWindowFocus: false});
    const growerIdList = getAllGrowersQuery.data;
    const [desiredGrower, setDesiredGrower] = useState<GrowerDTO>();
    
    const [desiredBin, setDesiredBin] = useState<BinInfoDTO | null>();
    const getBinsQuery = useQuery({...getBinsQueryOptions( desiredGrower?.growerID), enabled: true, refetchOnWindowFocus: false });
    const binIDList = getBinsQuery.data;
    const [showForm, setShowForm] = useState<boolean>(false);

    useEffect(() => {
        if(getAllGrowersQuery.isError){
            let error = getAllGrowersQuery.error as ApiError;
            notification.error({
                message: "Could not fetch grower IDs",
                description: error?.errorDetails?.detail ?? "Contact support"
            });
        }
    }, [getAllGrowersQuery.error]);

    useEffect(() => {
        if(getBinsQuery.isError){
            let error = getBinsQuery.error as ApiError;
            notification.error({
                message: "Could not fetch bins",
                description: error?.errorDetails?.detail ?? "Contact support"
            });
        }
    }, [getBinsQuery.error]);
    
    const handleGrowerIdClicked = (id: number) => {
        let grower = growerIdList.find( b=> b.growerID === id);
        setDesiredGrower(grower);
    }

    const handleBinIdClicked = (id: number) => {
        let bin = binIDList?.find( b=> b.id === id);
        setDesiredBin(bin);   
        setShowForm(true);
    }

    const [nameSearch, setNameSearch] = useState("");
    const nameSearchDelayedValue = useDebouncedCallback((value) => {
        setNameSearch(value);
    }, DEBOUNCE_TIME_MS);

    const columns: Array<ColumnProps<BinInfoDTO>> = [
        {
            hidden: true,
            dataIndex: 'id',
        },
        {
        title: "Bin Name",
        dataIndex: "name",
        key: 'name',
        sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        defaultSortOrder: 'ascend',
        render(value, record, index) {
            return <div style={{width: "100%"}} onClick={async () => {
                await handleBinIdClicked(record.id);
                setShowForm(true);
            }}>
                    <Typography.Link style={{userSelect: 'text'}}>{value}</Typography.Link>
                </div>
        },
    },
    {
        title: "Device ID",
        dataIndex: "deviceId",
        render(value, record, index) {
            return value ?? <NotAssignedTag />
        },
    },
    {
        title: "Organization",
        dataIndex: "growerName",
        render(value, record, index) {
            return value ?? <NotAssignedTag />
        },
    },
    {
        title: "System Type",
        dataIndex: "automationType",
        render(value, record, index) {
            return value ?? <NotAssignedTag />
        },
    }
];


    const updateFilters = () => {
        if (nameSearch === "") {
            return binIDList ?? [];
        }

        const filteredTable = binIDList
            ?.filter((record) => {
                const searchTerm = nameSearch.toUpperCase();
                return record.name?.toUpperCase().includes(searchTerm)
                || record.deviceId?.toUpperCase().includes(searchTerm)
                || record.growerName?.toUpperCase().includes(searchTerm);
                })
            ?? [];
        return filteredTable;
    };
    
    const filteredData = useMemo(() => updateFilters(), [nameSearch, binIDList]);




    

    return (
        <div style={{ width:"100%", minHeight: "70vh" }}>

            <Row gutter={[16, 16]} style={{ width:"100%" }}>
                        <Col span={6}>
                        <Flex gap={32} vertical>
                            <Select
                            style={{ width: "100%", maxHeight: "300px", overflow: "auto" }}
                            placeholder="Select Organization"
                            showSearch
                            allowClear
                            optionFilterProp="children" 
                            onSelect={ (val) => {
                                handleGrowerIdClicked(val)
                            }}
                            >   
                                <Select.Option value={null}>All Organizations</Select.Option>
                                    {growerIdList.map((grower) => (
                                        <Select.Option key={grower.growerID} value={grower.growerID}>
                                            {grower.growerName}
                                        </Select.Option>
                                    ))}
                            </Select>
                            <Button onClick={() => handleBinIdClicked(0)} 
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    block
                                >
                                    Create new bin
                                </Button>                
                                </Flex>
                        </Col>
                <Col span={16}>
                    <Input 
                    placeholder="Search bins"
                    onChange={(evt) => nameSearchDelayedValue(evt.target.value)}
                    style={{ width:"280px", marginBottom: '32px', marginRight: '8px'}}
                    />
                    <Table size="small" scroll={{ y: 372 }} pagination={false} columns={columns} dataSource={filteredData} />
                </Col>

            </Row>

            <Drawer key={desiredBin?.id} title={`Bin Details`} width={800} open={showForm} onClose={() => setShowForm(false)}>
                <AddOrCreateBinForm desiredBin={desiredBin!} setShowForm={(showForm: boolean) => setShowForm(showForm)} />
            </Drawer>

        </div>
    );
};
